import { useCallback, useEffect, useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { useAccountExpirationSnackbar } from '@components/newNavigation/useAccountExpirationSnackbar';
import { useConnectedProductsWarning } from '@components/newNavigation/useConnectedProductsWarning';
import { FeatureFlags } from '@fabric-ui/components';
import { QueryKey } from '@fabric-ui/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@utils/AvRouter';
import { avRum, shouldRecordSession } from '@utils/AvRum';
import { initializePendo } from '@utils/pendo/pendo.utils';
import { gql } from 'graphql-request';
import { useAuthContext } from './AuthContextProvider';
import { AccountType } from './AvContext.types';
import { AccountDto } from './types';

export const GET_ACCOUNT = gql`
  query findStartupAccountByContext {
    findStartupAccountByContext {
      name
      logo
      accountType
      createdAt
      expiresAt
      autoScheduleHour
      customerFacing
      apps {
        id
        name
      }
      aiFeatures {
        enabled
      }
    }
  }
`;

const GET_RECOMMENDATION_SETTINGS = gql`
  query getRecommendationSettings {
    getRecommendationSettings {
      enabled
    }
  }
`;

const GET_PROVISIONING_STATUS = gql`
  query getProvisioningStatus($accountId: String!) {
    getProvisioningStatus(accountId: $accountId)
  }
`;

const defaultAccount = {
  id: '',
  name: '',
  apps: [],
  logo: null,
  autoScheduleHour: null,
  accountType: AccountType.POC,
  createdAt: '',
  expiresAt: null,
  flags: { isRecommendedFieldsEnabled: false },
  aiFeatures: { enabled: true },
  customerFacing: false,
};

type AccountData = Omit<AccountDto, 'flags'> & { createdAt: string };

interface RecommendationSettingsData {
  enabled: boolean;
}

export const useInvalidateAccount = () => {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries({ queryKey: [QueryKey.account] }), [queryClient]);
};

export const useAccount = (options?) => {
  const navigate = useNavigate();
  const { accountId, featureFlags } = useAuthContext();

  const expirationSnackbar = useAccountExpirationSnackbar();
  const connectedProductsWarning = useConnectedProductsWarning();

  const {
    data: accountData,
    isPending: isAccountLoading,
    error: accountError,
    refetch: refetchAccount,
  } = useGqlQuery<AccountData>({
    queryKey: [QueryKey.account],
    query: GET_ACCOUNT,
    onSuccess: expirationSnackbar.onAccountSuccess,
    ...options,
  });

  useGqlQuery<string>({
    queryKey: [QueryKey.ConnectedProducts, 'provisioningStatus', accountId],
    query: GET_PROVISIONING_STATUS,
    variables: { accountId },
    enabled: !!accountId && !!featureFlags[FeatureFlags.ConnectedProductsFrontend],
    onSuccess: connectedProductsWarning.updateStatus,
  });

  const {
    data: recommendationSettingsData,
    isPending: isSettingsLoading,
    error: recommendationSettingsError,
    refetch: refetchSettings,
  } = useGqlQuery<RecommendationSettingsData>({
    queryKey: [QueryKey.recommendationSettings],
    query: GET_RECOMMENDATION_SETTINGS,
    ...options,
  });

  useEffect(() => {
    if (accountError) {
      console.error(accountError);
      // @ts-ignore
      if (accountError.response?.errors?.[0].extensions?.status === 404) {
        navigate('error-process-request');
      }
    }
  }, [accountError]);

  const data = useMemo(() => {
    if (!accountData || !recommendationSettingsData) {
      return undefined;
    }

    return {
      ...accountData,
      flags: { isRecommendedFieldsEnabled: recommendationSettingsData.enabled },
    };
  }, [accountData, recommendationSettingsData]);

  useEffect(() => {
    if (data && shouldRecordSession) {
      avRum.setUser({
        accountName: data.name,
        accountType: data.accountType,
        accountCreated: data.createdAt,
      });

      initializePendo(avRum.getUser() as any);
    }
  }, [data]);

  return {
    data: data || defaultAccount,
    isPending: isAccountLoading || isSettingsLoading,
    error: accountError || recommendationSettingsError,
    refetch: useCallback(() => Promise.all([refetchAccount(), refetchSettings()]), [refetchAccount, refetchSettings]),
  };
};
